import React, { useContext, ReactNode, useMemo } from 'react';

import PropTypes from 'prop-types';

import {
  CONTEXT_NAMES,
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';
import theme, { StyleProp, stylePropType } from '@adsk/alloy-react-theme';

import Notification from '../Notification';
import { useNotificationsStore, Notif } from './store';

const NotificationsContext = getOrCreateGlobalContext<NotificationsContextType>(
  CONTEXT_NAMES.NotificationsContext,
);

export type NotificationsContextType = {
  showNotification: (notification: Notif) => {
    id: string;
    removeNotification: () => void;
  };
  removeNotification: (id: string | number) => void;
};

export const useNotifications = (): NotificationsContextType => {
  // React context — same-version consumers get fiber-tree-scoped value.
  const contextValue = useContext(NotificationsContext);

  // Global Zustand store — cross-version fallback (React 18/19 MFEs calling into
  // a React 17 host's NotificationsProvider).
  const storeValue = useStoreWithValidation<NotificationsContextType>(
    CONTEXT_NAMES.NotificationsContext,
    {
      hook: 'useNotifications',
      provider: 'NotificationsProvider',
      shouldThrowError: false,
    },
  );

  const value = contextValue ?? storeValue;

  if (!value) {
    throw new Error(
      'useNotifications must be used within a NotificationsProvider',
    );
  }

  return value;
};

type NotificationsProviderProps = {
  /**
   * Overrides the position of the toast container.
   * Only applied by the **root** `NotificationsProvider` in the React tree;
   * nested providers inherit the root's position and ignore this prop.
   */
  style?: StyleProp;
  children?: ReactNode;
};

export function NotificationsProvider({
  style,
  children,
}: NotificationsProviderProps) {
  const { notifications, showNotification, removeNotification } =
    useNotificationsStore();

  // useContext reads from the nearest ancestor fiber, before this provider
  // wraps its own subtree — so undefined means no ancestor provider exists.
  const isRoot = useContext(NotificationsContext) === undefined;

  const value = useMemo(
    () => ({
      showNotification,
      removeNotification,
    }),
    [showNotification, removeNotification],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      {isRoot && (
        <div
          css={[
            {
              position: 'fixed',
              zIndex: theme.zIndex.notification,
              right: 16,
              top: 16 + 72 /* header height is 72 */,
            },
            style,
          ]}
        >
          {notifications.map((notification) => {
            const {
              message,
              id,
              // this is unused in the Notification component
              autoDismiss: _ignoredAutoDismiss,
              style: notificationStyle,
              ...rest
            } = notification;

            return (
              <Notification
                {...rest}
                key={id}
                style={[{ marginBottom: theme.spacing.XS }, notificationStyle]}
              >
                {message}
              </Notification>
            );
          })}
        </div>
      )}
    </NotificationsContext.Provider>
  );
}

NotificationsProvider.propTypes = {
  style: stylePropType,
  children: PropTypes.any,
};
